import React from 'react';

type SimpleMarkdownProps = {
  text: string;
  keyPrefix: string;
  wrapperClassName?: string;
};

const parseSizedLine = (line: string) => {
  const trimmed = line.trimStart();

  if (trimmed.startsWith('###')) {
    return {
      content: trimmed.slice(4),
      sizeClass: 'card-markdown-size-small',
    };
  }

  if (trimmed.startsWith('##')) {
    return {
      content: trimmed.slice(3),
      sizeClass: 'card-markdown-size-medium',
    };
  }

  if (trimmed.startsWith('#')) {
    return {
      content: trimmed.slice(2),
      sizeClass: 'card-markdown-size-large',
    };
  }

  return {
    content: line,
    sizeClass: '',
  };
};

const parseInlineMarkdown = (line: string, keyPrefix: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const tokenRegex = /(\*\*\*[^*]+?\*\*\*|\*\*[^*]+?\*\*|\*[^*]+?\*|_[^_]+_)/g;
  let lastIndex = 0;
  let tokenIndex = 0;
  let match = tokenRegex.exec(line);

  while (match) {
    const index = match.index;
    const token = match[0];

    if (index > lastIndex) {
      parts.push(line.slice(lastIndex, index));
    }

    if (token.startsWith('***') && token.endsWith('***')) {
      const content = token.slice(3, -3);
      parts.push(
        <strong key={`${keyPrefix}-strong-italic-${tokenIndex}`}>
          <em>{content}</em>
        </strong>
      );
    } else if (token.startsWith('**') && token.endsWith('**')) {
      const content = token.slice(2, -2);
      parts.push(<strong key={`${keyPrefix}-strong-${tokenIndex}`}>{content}</strong>);
    } else if (
      (token.startsWith('*') && token.endsWith('*')) ||
      (token.startsWith('_') && token.endsWith('_'))
    ) {
      const content = token.slice(1, -1);
      parts.push(<em key={`${keyPrefix}-italic-${tokenIndex}`}>{content}</em>);
    } else {
      parts.push(token);
    }

    lastIndex = index + token.length;
    tokenIndex += 1;
    match = tokenRegex.exec(line);
  }

  if (lastIndex < line.length) {
    parts.push(line.slice(lastIndex));
  }

  return parts;
};

export const SimpleMarkdown: React.FC<SimpleMarkdownProps> = ({ text, keyPrefix, wrapperClassName }) => {
  if (!text) {
    return null;
  }

  const className = wrapperClassName ? `description-wrapper ${wrapperClassName}` : 'description-wrapper';

  return (
    <div className={className}>
      {text.replace(/\r\n/g, '\n').split('\n').map((line, index) => {
        const { content, sizeClass } = parseSizedLine(line);
        const lineClassName = `card-markdown-line${sizeClass ? ` ${sizeClass}` : ''}`;

        return (
          <span className={lineClassName} key={`${keyPrefix}-line-${index}`}>
            {parseInlineMarkdown(content, `${keyPrefix}-line-${index}`)}
          </span>
        );
      })}
    </div>
  );
};

